/**
 * Cross-provider failover: the reusable core of Elyra's "AI sovereignty"
 * behavior. When a turn fails because a provider is unavailable (overloaded,
 * rate-limited, an auth problem, or a model that vanished — e.g. an
 * export-control suspension), we can re-run the turn on an equivalent model
 * from a *different* provider instead of just surfacing the error.
 *
 * This is only possible because Elyra is provider-agnostic. A single-vendor
 * agent has nowhere to fail over to.
 *
 * Both functions here are pure so they can be unit-tested without real
 * providers, and reused by cross-provider consensus (#1) and per-repo model
 * shopping (#4), which need the same equivalence notion.
 */

/** Minimal model shape the fallback selector needs (Model<Api> satisfies it). */
export interface FallbackModelInfo {
	provider: string;
	id: string;
	reasoning: boolean;
	input: readonly string[];
	cost: { output: number };
	contextWindow: number;
}

export type FailureClass = "failover" | "fatal";

/** Stable key for de-duping tried models across failover attempts. */
export function modelKey(m: { provider: string; id: string }): string {
	return `${m.provider}/${m.id}`;
}

/** Extract an HTTP-ish status code from an unknown provider error, if present. */
function extractStatus(error: unknown): number | undefined {
	if (typeof error !== "object" || error === null) return undefined;
	const record = error as Record<string, unknown>;
	const raw = record.status ?? record.statusCode ?? record.code;
	if (typeof raw === "number") return raw;
	if (typeof raw === "string" && /^\d+$/.test(raw)) return Number.parseInt(raw, 10);
	return undefined;
}

function errorMessage(error: unknown): string {
	return error instanceof Error ? error.message : String(error);
}

/** Statuses that mean "this provider can't serve the request right now". */
const FAILOVER_STATUSES = new Set([401, 403, 404, 408, 409, 425, 429, 500, 502, 503, 504, 529]);
/** Statuses that will fail identically on any provider — do not fail over. */
const FATAL_STATUSES = new Set([400, 413, 422]);

/** Message fragments that indicate a transient/provider-specific failure. */
const FAILOVER_PATTERNS = [
	"overloaded",
	"rate limit",
	"rate-limit",
	"capacity",
	"not available",
	"temporarily unavailable",
	"timeout",
	"timed out",
	"econnreset",
	"etimedout",
	"enotfound",
	"fetch failed",
	"socket hang up",
	"service unavailable",
];

/** Message fragments that indicate a request that no provider will accept. */
const FATAL_PATTERNS = [
	"context length",
	"maximum context",
	"context window",
	"too many tokens",
	"prompt is too long",
	"maximum number of tokens",
	"invalid request",
	"unsupported",
];

/**
 * Decide whether a failed turn should be retried on a different provider.
 *
 * Conservative on the fatal side: an unrecognized error with no transient
 * signal is treated as fatal (surfaced as today) rather than burning a
 * failover attempt on what is probably a real bug or malformed request.
 */
export function classifyProviderFailure(error: unknown): FailureClass {
	const status = extractStatus(error);
	if (status !== undefined) {
		if (FATAL_STATUSES.has(status)) return "fatal";
		if (FAILOVER_STATUSES.has(status)) return "failover";
	}

	const message = errorMessage(error).toLowerCase();
	if (FATAL_PATTERNS.some((p) => message.includes(p))) return "fatal";
	if (FAILOVER_PATTERNS.some((p) => message.includes(p))) return "failover";

	return "fatal";
}

/** Coarse capability band by output cost ($/million tokens). */
export function costBand(outputCost: number): 0 | 1 | 2 {
	if (outputCost < 3) return 0;
	if (outputCost < 20) return 1;
	return 2;
}

/**
 * Minimal reliability measurements the fallback selector can consume
 * (structurally satisfied by routing-ledger's ModelOutcomeStats).
 */
export interface ModelReliabilityStats {
	turns: number;
	successRate: number;
	failoversFrom: number;
}

/** Below this many measured turns a model is never demoted. */
const MIN_RELIABILITY_SAMPLE = 5;

/**
 * Coarse demotion bucket from local outcome measurements: 0 = fine (or not
 * enough data), 1 = shaky (success < 80%), 2 = unreliable (success < 50% or
 * we failed over away from it more often than not). Models without
 * sufficient samples rank exactly as before — measurements only ever demote
 * demonstrated problems, never promote on thin evidence.
 */
function reliabilityPenalty(m: FallbackModelInfo, stats?: ReadonlyMap<string, ModelReliabilityStats>): number {
	const s = stats?.get(modelKey(m));
	if (!s || s.turns < MIN_RELIABILITY_SAMPLE) return 0;
	const failoverRate = s.failoversFrom / s.turns;
	if (s.successRate < 0.5 || failoverRate > 0.5) return 2;
	if (s.successRate < 0.8) return 1;
	return 0;
}

/**
 * Pick an equivalent model to fail over to, or null when none is suitable.
 *
 * Ranking, best first:
 *   1. A different provider (the whole point — route around the outage).
 *   2. Measured reliability (when a local routing ledger is supplied):
 *      models that demonstrably failed here are demoted, with a sample-size
 *      floor so thin data never changes the ranking.
 *   3. Same capability band as the current model (don't silently downgrade
 *      a powerful turn to a fast model, or overpay by jumping up a band).
 *   4. Reasoning capability matching the current model.
 *   5. Closest output cost.
 *   6. Larger context window as a final tiebreak.
 *
 * Image conversations only ever fall over to vision-capable models, so a
 * multimodal session can never break.
 */
export function selectFallbackModel<T extends FallbackModelInfo>(
	current: FallbackModelInfo,
	available: readonly T[],
	alreadyTried: ReadonlySet<string> = new Set(),
	stats?: ReadonlyMap<string, ModelReliabilityStats>,
): T | null {
	const needsImage = current.input.includes("image");
	const currentBand = costBand(current.cost.output);

	const candidates = available.filter((m) => {
		if (modelKey(m) === modelKey(current)) return false;
		if (alreadyTried.has(modelKey(m))) return false;
		if (needsImage && !m.input.includes("image")) return false;
		return true;
	});

	if (candidates.length === 0) return null;

	const rank = (m: FallbackModelInfo): number[] => [
		m.provider === current.provider ? 1 : 0, // different provider first
		reliabilityPenalty(m, stats), // measured reliability (demotion only)
		Math.abs(costBand(m.cost.output) - currentBand), // same band preferred
		m.reasoning === current.reasoning ? 0 : 1, // reasoning match
		Math.abs(m.cost.output - current.cost.output), // closest cost
		-m.contextWindow, // larger context wins
	];

	let best = candidates[0];
	let bestRank = rank(best);
	for (let i = 1; i < candidates.length; i++) {
		const r = rank(candidates[i]);
		if (lexLess(r, bestRank)) {
			best = candidates[i];
			bestRank = r;
		}
	}
	return best;
}

/** Lexicographic comparison of two numeric rank tuples: a < b ? */
function lexLess(a: number[], b: number[]): boolean {
	for (let i = 0; i < Math.max(a.length, b.length); i++) {
		const av = a[i] ?? 0;
		const bv = b[i] ?? 0;
		if (av !== bv) return av < bv;
	}
	return false;
}
